import { useCallback, useEffect, useRef, useState } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Table,
  Alert,
  Spinner,
} from "react-bootstrap";
import moment from "moment";

import MediosDePago from "./MediosDePago";
import { getCarteraDisponibles } from "../helpers/rutaCarteras";

import {
  postOrdenPagoRemito,
  getPendientesOrdenPagoRemito,
  getCreditosDisponiblesOrdenPagoRemito,
} from "../helpers/rutaOrdenesPagoRemito";

const dinero = (valor) =>
  Number(valor || 0).toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
  });

const centavos = (valor) => {
  const n = Number(valor === "" ? 0 : valor);

  if (!Number.isFinite(n) || n < 0) return null;

  const c = Math.round(n * 100);

  if (
    !Number.isSafeInteger(c) ||
    Math.abs(n * 100 - c) > 0.000001
  ) {
    return null;
  }

  return c;
};

// Mismo criterio de presentación que el listado de remitos.
const fechaRemito = (valor) => {
  if (!valor) return "-";

  const s = String(valor);

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.slice(0, 10).split("-");
    return `${d}/${m}/${y}`;
  }

  return moment.utc(valor).isValid()
    ? moment.utc(valor).format("DD/MM/YYYY")
    : "-";
};

function fechaAPI(valor) {
  if (!valor) return undefined;

  const s = String(valor);

  return /^\d{4}-\d{2}-\d{2}$/.test(s)
    ? `${s}T00:00:00-03:00`
    : s;
}

function serializarPago(m, fechaOrden) {
  const tipos = {
    EFECTIVO: "efectivo",
    TRANSFERENCIA: "transferencia",
    CHEQUE: "cheque",
    CHEQUE_TERCERO: "cheque",
    CHEQUE_PROPIO: "cheque",
    ICHEQ: "echeq",
    ECHEQ: "echeq",
  };

  const medio = tipos[String(m.tipo || "").trim().toUpperCase()];
  const importe = centavos(m.importe);

  if (!medio) {
    throw new Error("Hay un medio de pago sin tipo válido.");
  }

  if (importe === null || importe <= 0) {
    throw new Error("Los pagos deben tener importes mayores que cero.");
  }

  const pago = {
    medio,
    importe: importe / 100,
  };

  if (medio === "efectivo") {
    if (!m.caja) throw new Error("Seleccione la caja del pago.");

    pago.caja = m.caja;
  }

  if (medio === "transferencia") {
    if (!m.cuentaBancaria) {
      throw new Error("Seleccione la cuenta bancaria del pago.");
    }

    pago.cuentaBancaria = m.cuentaBancaria;
    pago.referencia =
      m.refTransferencia || m.referencia || m.numero || "";
  }

  if (medio === "cheque") {
    if (!m.nroCheque) {
      throw new Error("Ingrese el número del cheque.");
    }

    Object.assign(pago, {
      nroCheque: m.nroCheque,
      bancoEmisor: m.bancoEmisor || "",
      librador: m.librador || "",
      cuitLibrador: m.cuitLibrador || "",
      fechaCheque: fechaAPI(m.fecha || fechaOrden),
      fechaPagoDiferido: fechaAPI(m.fechaPagoDiferido),
    });
  }

  if (medio === "echeq") {
    if (!m.idICheq) throw new Error("Ingrese el ID del eCheq.");

    Object.assign(pago, {
      idICheq: m.idICheq,
      bancoEmisor: m.bancoEmisor || "",
      emisor: m.emisor || "",
      cuitEmisor: m.cuitEmisor || "",
      fechaLiquidacion: fechaAPI(m.fechaLiquidacion),
    });
  }

  // El backend genera el _id definitivo del pago.
  return pago;
}

const estiloCheck = {
  position: "static",
  float: "none",
  display: "inline-block",
  margin: 0,
  width: 16,
  height: 16,
  verticalAlign: "middle",
  cursor: "pointer",
  accentColor: "#0d6efd",
};

export default function ModalOrdenPagoRemito({
  show,
  onHide,
  proveedor,
}) {
  const proveedorId =
    typeof proveedor === "string" ? proveedor : proveedor?._id;

  const [pendientes, setPendientes] = useState([]);
  const [creditos, setCreditos] = useState([]);
  const [medios, setMedios] = useState([]);
  const [retenciones, setRetenciones] = useState([]);

  const [cartera, setCartera] = useState([]);
  const [valoresSeleccionados, setValoresSeleccionados] = useState([]);
  const [cargandoCartera, setCargandoCartera] = useState(false);
  const [errorCartera, setErrorCartera] = useState("");

  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [observaciones, setObservaciones] = useState("");
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [cargaCompleta, setCargaCompleta] = useState(false);
  const [mediosValidos, setMediosValidos] = useState(false);
  const [error, setError] = useState("");

  const enviando = useRef(false);

  const onValidityChange = useCallback(({ valid }) => {
    setMediosValidos(valid);
  }, []);

  useEffect(() => {
    if (!show) return undefined;

    let cancelado = false;

    setPendientes([]);
    setCreditos([]);
    setMedios([]);
    setRetenciones([]);
    setValoresSeleccionados([]);
    setFecha(moment().format("YYYY-MM-DD"));
    setObservaciones("");
    setMediosValidos(false);
    setError("");
    setCargaCompleta(false);

    if (!proveedorId) {
      setCargando(false);
      setError("Seleccione un proveedor para registrar el pago.");
      return undefined;
    }

    setCargando(true);

    async function cargar() {
      try {
        const [resPendientes, resCreditos] = await Promise.all([
          getPendientesOrdenPagoRemito(proveedorId),
          getCreditosDisponiblesOrdenPagoRemito(proveedorId),
        ]);

        if (cancelado) return;

        if (!resPendientes?.ok) {
          throw new Error(
            resPendientes?.msg || "No se pudieron cargar los remitos."
          );
        }

        if (!resCreditos?.ok) {
          throw new Error(
            resCreditos?.msg || "No se pudieron cargar los anticipos."
          );
        }

        setPendientes(
          (resPendientes.pendientes || []).map((r) => ({
            ...r,
            seleccionado: false,
            aplicadoA: "",
            aplicadoB: "",
            aplicadoFlete: "",
          }))
        );

        setCreditos(
          (resCreditos.creditos || []).map((c) => ({
            ...c,
            importe: "",
          }))
        );

        setCargaCompleta(true);
      } catch (e) {
        if (!cancelado) setError(e.message);
      } finally {
        if (!cancelado) setCargando(false);
      }
    }

    cargar();

    return () => {
      cancelado = true;
    };
  }, [show, proveedorId]);

  useEffect(() => {
    if (!show) return undefined;

    let cancelado = false;

    setCartera([]);
    setErrorCartera("");
    setCargandoCartera(true);

    async function cargar() {
      try {
        const res = await getCarteraDisponibles({
          estados: "EN_CARTERA",
        });

        if (cancelado) return;

        if (res?.ok === false) {
          throw new Error("No se pudo cargar la cartera disponible.");
        }

        const lista = Array.isArray(res)
          ? res
          : res?.items ||
            res?.cartera ||
            res?.valores ||
            res?.data ||
            [];

        if (!Array.isArray(lista)) {
          throw new Error("La respuesta de cartera no contiene una lista.");
        }

        setCartera(
          lista.filter(
            (v) =>
              String(v.estado || "").toUpperCase() === "EN_CARTERA" &&
              ["CHEQUE", "ICHEQ"].includes(
                String(v.tipo || "").toUpperCase()
              ) &&
              !v.opId &&
              !v.opRemitoId
          )
        );
      } catch (e) {
        if (!cancelado) setErrorCartera(e.message);
      } finally {
        if (!cancelado) setCargandoCartera(false);
      }
    }

    cargar();

    return () => {
      cancelado = true;
    };
  }, [show, proveedorId]);

  function cambiarRemito(id, patch) {
    setPendientes((actuales) =>
      actuales.map((r) => (r._id === id ? { ...r, ...patch } : r))
    );
  }

  function seleccionarRemito(r, seleccionado) {
    cambiarRemito(r._id, {
      seleccionado,
      aplicadoA: seleccionado ? Math.max(0, Number(r.saldoA || 0)) : "",
      aplicadoB: seleccionado ? Math.max(0, Number(r.saldoB || 0)) : "",
      aplicadoFlete: seleccionado
        ? Math.max(0, Number(r.saldoFlete || 0))
        : "",
    });
  }

  function seleccionarTodos(seleccionado) {
    setPendientes((actuales) =>
      actuales.map((r) => ({
        ...r,
        seleccionado,
        aplicadoA: seleccionado ? Math.max(0, Number(r.saldoA || 0)) : "",
        aplicadoB: seleccionado ? Math.max(0, Number(r.saldoB || 0)) : "",
        aplicadoFlete: seleccionado
          ? Math.max(0, Number(r.saldoFlete || 0))
          : "",
      }))
    );
  }

  function cambiarCredito(id, importe) {
    setCreditos((actuales) =>
      actuales.map((c) => (c._id === id ? { ...c, importe } : c))
    );
  }

  function cambiarRetencion(indice, patch) {
    setRetenciones((actuales) =>
      actuales.map((r, i) => (i === indice ? { ...r, ...patch } : r))
    );
  }

  function seleccionarValor(id, seleccionado) {
    setValoresSeleccionados((actuales) =>
      seleccionado
        ? [...new Set([...actuales, id])]
        : actuales.filter((valorId) => valorId !== id)
    );
  }

  const seleccionados = pendientes.filter((r) => r.seleccionado);
  const valores = cartera.filter((v) =>
    valoresSeleccionados.includes(v._id)
  );

  const sumar = (lista, campo) =>
    lista.reduce((total, item) => total + (centavos(item[campo]) ?? 0), 0);

  const aplicadoA = sumar(seleccionados, "aplicadoA");
  const aplicadoB = sumar(seleccionados, "aplicadoB");
  const aplicadoFlete = sumar(seleccionados, "aplicadoFlete");

  const totalAplicado = aplicadoA + aplicadoB + aplicadoFlete;
  const totalMedios = sumar(medios, "importe");
  const totalCartera = sumar(valores, "importe");
  const totalRetenciones = sumar(retenciones, "importe");
  const totalCreditos = sumar(creditos, "importe");

  const totalPagos = totalMedios + totalCartera;
  const disponible = totalPagos + totalRetenciones + totalCreditos;
  const diferencia = disponible - totalAplicado;

  let validacion = "";

  for (const r of seleccionados) {
    let totalRemito = 0;

    for (const parte of ["A", "B", "Flete"]) {
      const importe = centavos(r[`aplicado${parte}`]);
      const saldo = centavos(r[`saldo${parte}`] ?? 0);

      if (importe === null || saldo === null) {
        validacion =
          "Revise los importes: deben ser positivos o cero y tener hasta dos decimales.";
        break;
      }

      if (importe > saldo) {
        validacion =
          `La aplicación ${parte} supera el saldo del remito ${r.codrem}.`;
        break;
      }

      totalRemito += importe;
    }

    if (validacion) break;

    if (totalRemito === 0) {
      validacion =
        `Ingrese un importe o desmarque el remito ${r.codrem}.`;
      break;
    }
  }

  for (const c of creditos) {
    const importe = centavos(c.importe);

    if (
      importe === null ||
      importe > (centavos(c.disponible) ?? 0)
    ) {
      validacion = `Importe de anticipo inválido en ${c.numero}.`;
      break;
    }
  }

  for (const r of retenciones) {
    const importe = centavos(r.importe);

    if (importe === null || importe <= 0) {
      validacion = "Complete las retenciones o quite las filas vacías.";
      break;
    }
  }

  if (
    medios.some((m) => {
      const importe = centavos(m.importe);
      return importe === null || importe <= 0;
    })
  ) {
    validacion = "Revise los importes de los medios de pago.";
  }

  if (
    valores.some((v) => {
      const importe = centavos(v.importe);
      return importe === null || importe <= 0;
    })
  ) {
    validacion = "Hay un valor de cartera con importe inválido.";
  }

  if (!validacion && totalCreditos > totalAplicado) {
    validacion =
      "Los anticipos utilizados no pueden superar lo aplicado a remitos.";
  }

  if (!validacion && diferencia < 0) {
    validacion =
      "Los pagos, retenciones y anticipos no cubren lo aplicado.";
  }

  const puedeGuardar =
    Boolean(proveedorId) &&
    Boolean(fecha) &&
    cargaCompleta &&
    !cargando &&
    !guardando &&
    !validacion &&
    disponible > 0 &&
    (medios.length === 0 || mediosValidos);

  async function guardar() {
    if (!puedeGuardar || enviando.current) return;

    enviando.current = true;
    setGuardando(true);
    setError("");

    try {
      const payload = {
        proveedor: proveedorId,
        fecha,
        observaciones,

        aplicaciones: seleccionados.map((r) => ({
          remito: r._id,
          aplicadoA: centavos(r.aplicadoA) / 100,
          aplicadoB: centavos(r.aplicadoB) / 100,
          aplicadoFlete: centavos(r.aplicadoFlete) / 100,
        })),

        saldosFavorAplicados: creditos
          .filter((c) => (centavos(c.importe) || 0) > 0)
          .map((c) => ({
            ordenPagoOrigen: c._id,
            importe: centavos(c.importe) / 100,
          })),

        pagos: [
          ...medios.map((m) => serializarPago(m, fecha)),
          ...valores.map((v) => ({
            medio:
              String(v.tipo).toUpperCase() === "ICHEQ"
                ? "echeq"
                : "cheque",
            importe: centavos(v.importe) / 100,
            carteraItem: v._id,
          })),
        ],

        retenciones: retenciones.map((r) => ({
          tipo: r.tipo,
          comprobante: r.comprobante || "",
          importe: centavos(r.importe) / 100,
        })),
      };

      const respuesta = await postOrdenPagoRemito(payload);

      if (!respuesta?.ok) {
        throw new Error(
          respuesta?.msg || "No se pudo guardar la orden de pago."
        );
      }

      setCargaCompleta(false);
      onHide?.(true, respuesta.orden);
    } catch (e) {
      setError(e.message);
    } finally {
      enviando.current = false;
      setGuardando(false);
    }
  }

  function cerrar() {
    if (!enviando.current) onHide?.(false);
  }

  return (
    <Modal
      show={show}
      onHide={cerrar}
      size="xl"
      backdrop="static"
      keyboard={!guardando}
    >
      <Modal.Header closeButton={!guardando}>
        <Modal.Title>Nueva orden de pago · Papas</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {cargando && (
          <div className="mb-3">
            <Spinner animation="border" size="sm" /> Cargando saldos…
          </div>
        )}

        <fieldset disabled={cargando || guardando || !cargaCompleta}>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Label>Proveedor</Form.Label>
              <Form.Control
                value={
                  proveedor?.razonsocial ||
                  proveedor?.nombre ||
                  proveedor?.proveedor ||
                  proveedorId ||
                  ""
                }
                readOnly
              />
            </Col>

            <Col md={3}>
              <Form.Label>Fecha</Form.Label>
              <Form.Control
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
              />
            </Col>

            <Col md={3}>
              <Form.Label>Número de OP</Form.Label>
              <Form.Control value="Automático al guardar" readOnly />
            </Col>
          </Row>

          <div
            className="p-3 mb-3 rounded"
            style={{ background: "#edf4ff", color: "#17488b" }}
          >
            <Row>
              <Col md={3}>
                Aplicado A: <strong>{dinero(aplicadoA / 100)}</strong>
              </Col>
              <Col md={3}>
                Aplicado B: <strong>{dinero(aplicadoB / 100)}</strong>
              </Col>
              <Col md={3}>
                Flete: <strong>{dinero(aplicadoFlete / 100)}</strong>
              </Col>
              <Col md={3}>
                Total: <strong>{dinero(totalAplicado / 100)}</strong>
              </Col>
            </Row>
          </div>

          <div className="d-flex flex-wrap gap-2 mb-2">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={() => seleccionarTodos(true)}
            >
              Seleccionar todos
            </Button>

            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => seleccionarTodos(false)}
            >
              Limpiar selección
            </Button>
          </div>

          <div style={{ maxHeight: 320, overflow: "auto" }}>
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th style={{ minWidth: 48, textAlign: "center" }}>
                    Sel.
                  </th>
                  <th>Remito</th>
                  <th>Fecha</th>
                  <th>Saldo A</th>
                  <th>Saldo B</th>
                  <th>Saldo flete</th>
                  <th>Aplicar A</th>
                  <th>Aplicar B</th>
                  <th>Aplicar flete</th>
                </tr>
              </thead>

              <tbody>
                {pendientes.map((r) => (
                  <tr key={r._id}>
                    <td
                      style={{
                        minWidth: 48,
                        textAlign: "center",
                        verticalAlign: "middle",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(r.seleccionado)}
                        aria-label={`Seleccionar remito ${r.codrem}`}
                        style={estiloCheck}
                        onChange={(e) =>
                          seleccionarRemito(r, e.target.checked)
                        }
                      />
                    </td>

                    <td>{r.codrem}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {fechaRemito(r.fecha)}
                    </td>

                    {["A", "B", "Flete"].map((parte) => (
                      <td
                        key={`saldo-${parte}`}
                        className="text-end"
                        style={{ whiteSpace: "nowrap" }}
                      >
                        {dinero(r[`saldo${parte}`])}
                      </td>
                    ))}

                    {["A", "B", "Flete"].map((parte) => (
                      <td key={parte} style={{ minWidth: 120 }}>
                        <Form.Control
                          size="sm"
                          type="number"
                          min="0"
                          step="0.01"
                          max={Math.max(
                            0,
                            Number(r[`saldo${parte}`] || 0)
                          )}
                          disabled={!r.seleccionado}
                          value={r[`aplicado${parte}`]}
                          aria-label={`Aplicar ${parte} remito ${r.codrem}`}
                          onChange={(e) =>
                            cambiarRemito(r._id, {
                              [`aplicado${parte}`]: e.target.value,
                            })
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))}

                {!cargando && pendientes.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center">
                      No hay remitos pendientes. Puede registrar un anticipo.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {creditos.length > 0 && (
            <>
              <h6 className="mt-3">Anticipos disponibles del proveedor</h6>

              <Table bordered size="sm" responsive>
                <thead>
                  <tr>
                    <th>OP de origen</th>
                    <th>Disponible</th>
                    <th>Importe a utilizar</th>
                  </tr>
                </thead>

                <tbody>
                  {creditos.map((c) => (
                    <tr key={c._id}>
                      <td>{c.numero}</td>
                      <td>{dinero(c.disponible)}</td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="number"
                          min="0"
                          step="0.01"
                          max={c.disponible}
                          value={c.importe}
                          onChange={(e) =>
                            cambiarCredito(c._id, e.target.value)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}

          <div className="mt-3" style={{ overflowX: "auto" }}>
            {show && proveedorId && cargaCompleta && (
              <MediosDePago
                key={proveedorId}
                medios={medios}
                setMedios={setMedios}
                onValidityChange={onValidityChange}
              />
            )}
          </div>

          <div className="mt-3">
            <h6>Valores de cartera para entregar / endosar</h6>

            {cargandoCartera && (
              <div>
                <Spinner animation="border" size="sm" /> Cargando cartera…
              </div>
            )}

            {errorCartera && (
              <Alert variant="warning">{errorCartera}</Alert>
            )}

            {!cargandoCartera && !errorCartera && cartera.length === 0 && (
              <div className="text-muted">
                No hay valores disponibles en cartera.
              </div>
            )}

            {cartera.length > 0 && (
              <div style={{ maxHeight: 200, overflow: "auto" }}>
                <Table striped bordered size="sm">
                  <thead>
                    <tr>
                      <th style={{ minWidth: 48, textAlign: "center" }}>
                        Sel.
                      </th>
                      <th>Tipo</th>
                      <th>Número / ID</th>
                      <th>Banco</th>
                      <th>Recibido de</th>
                      <th>Importe</th>
                    </tr>
                  </thead>

                  <tbody>
                    {cartera.map((v) => (
                      <tr key={v._id}>
                        <td style={{ textAlign: "center" }}>
                          <input
                            type="checkbox"
                            style={estiloCheck}
                            checked={valoresSeleccionados.includes(v._id)}
                            aria-label={`Seleccionar valor ${
                              v.nro || v.nroCheque || v.idICheq || v._id
                            }`}
                            onChange={(e) =>
                              seleccionarValor(v._id, e.target.checked)
                            }
                          />
                        </td>
                        <td>{v.tipo}</td>
                        <td>{v.nro || v.nroCheque || v.idICheq || "-"}</td>
                        <td>{v.bancoEmisor || v.banco || "-"}</td>
                        <td>{v.cobradoA || v.librador || v.emisor || "-"}</td>
                        <td className="text-end">{dinero(v.importe)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}

            {valores.length > 0 && (
              <div className="text-end">
                Total cartera: <strong>{dinero(totalCartera / 100)}</strong>
              </div>
            )}
          </div>

          <div className="d-flex align-items-center gap-2 mt-3 mb-2">
            <h6 className="mb-0">Retenciones</h6>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() =>
                setRetenciones((actuales) => [
                  ...actuales,
                  { tipo: "IIBB", comprobante: "", importe: "" },
                ])
              }
            >
              Agregar retención
            </Button>
          </div>

          {retenciones.map((r, i) => (
            <Row className="mb-2" key={i}>
              <Col md={3}>
                <Form.Select
                  value={r.tipo}
                  onChange={(e) =>
                    cambiarRetencion(i, { tipo: e.target.value })
                  }
                >
                  <option value="IIBB">IIBB</option>
                  <option value="GAN">Ganancias</option>
                  <option value="IVA">IVA</option>
                  <option value="OTRA">Otra</option>
                </Form.Select>
              </Col>

              <Col md={5}>
                <Form.Control
                  placeholder="N.º comprobante"
                  value={r.comprobante}
                  onChange={(e) =>
                    cambiarRetencion(i, { comprobante: e.target.value })
                  }
                />
              </Col>

              <Col md={3}>
                <Form.Control
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="Importe"
                  value={r.importe}
                  onChange={(e) =>
                    cambiarRetencion(i, { importe: e.target.value })
                  }
                />
              </Col>

              <Col md={1}>
                <Button
                  variant="outline-danger"
                  aria-label="Quitar retención"
                  onClick={() =>
                    setRetenciones((actuales) =>
                      actuales.filter((_, indice) => indice !== i)
                    )
                  }
                >
                  ×
                </Button>
              </Col>
            </Row>
          ))}

          <Form.Group className="mt-3">
            <Form.Label>Observaciones</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
            />
          </Form.Group>
        </fieldset>

        <div
          className="mt-3 p-3 rounded"
          style={{ background: "#edf4ff", color: "#17488b" }}
        >
          <Row>
            <Col md={6}>
              <div>
                Pagos: <strong>{dinero(totalMedios / 100)}</strong>
              </div>
              <div>
                Valores de cartera:{" "}
                <strong>{dinero(totalCartera / 100)}</strong>
              </div>
              <div>
                Retenciones:{" "}
                <strong>{dinero(totalRetenciones / 100)}</strong>
              </div>
            </Col>

            <Col md={6}>
              <div>
                Anticipos utilizados:{" "}
                <strong>{dinero(totalCreditos / 100)}</strong>
              </div>
              <div>
                Aplicado a remitos:{" "}
                <strong>{dinero(totalAplicado / 100)}</strong>
              </div>
              <div>
                Saldo a favor generado:{" "}
                <strong>{dinero(Math.max(0, diferencia) / 100)}</strong>
              </div>
            </Col>
          </Row>
        </div>

        {validacion && (
          <Alert variant="warning" className="mt-3 mb-0">
            {validacion}
          </Alert>
        )}

        {medios.length > 0 && !mediosValidos && (
          <div className="text-danger mt-2">
            Complete los datos obligatorios de los medios de pago.
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          disabled={guardando}
          onClick={cerrar}
        >
          Cancelar
        </Button>

        <Button
          variant="primary"
          disabled={!puedeGuardar}
          onClick={guardar}
        >
          {guardando ? "Guardando…" : "Guardar orden de pago"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}